import TableList from "../components/TableList";
import {
  SurveyEventSubmit,
  SurveyEventStatusEnum,
} from "../models/survey.event.model";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { filterOnKeys, titleStr } from "../lib/helper";
import { FilterGroup, FilterType } from "../components/TableFilter";
import { HeadCell, Pagination, PaginationFilterType } from "../models/base.model";
import {
  deleteSurveyEventSubmit,
  getSubmitAnswers,
  getSurveyEventSubmitsById,
} from "../actions/survey.event.action";
import { Button, Chip, Stack } from "@mui/material";
import TooltipForForm from "../components/TooltipForForm";
import { getItemsNode } from "../components/EllipsisText";
import moment from "moment";
import SurveySubmitAnswerView from "./SurveySubmitAnswerView";
import { useConfig } from "./ConfigContext";
import PaginatedTableList from "../components/PaginatedTableList";
import LoadingButton from "../components/LoadingButton";

const status_type_color_map: { [key in SurveyEventStatusEnum]: any } = {
  draft: "default",
  sent: "warning",
  viewed: "info",
  answered: "success",
};

export default forwardRef((props: { id: string; companyId: string }, ref) => {
  const { localized } = useConfig();
  const headCells: readonly HeadCell[] = [
    {
      id: "id",
      label: "Submit ID",
    },
    {
      id: "uid",
      label: localized("Customer ID"),
    },
    {
      id: "name",
      label: localized("Customer Name"),
    },
    {
      id: "device",
      label: localized("Device Info"),
      render: (v) => {
        let device;
        try {
          if (v) {
            device = JSON.parse(v);
          }
        } catch (error) {
          console.warn("parse device fail", error);
        }
        return (
          <TooltipForForm title={getItemsNode(v)}>
            <span>{device?.vendor}</span>
          </TooltipForForm>
        );
      },
    },
    {
      id: "status",
      label: localized("Status"),
      render: (v: SurveyEventStatusEnum) => (
        <Chip color={status_type_color_map[v]} label={titleStr(v)} />
      ),
    },
    {
      id: "date",
      label: localized("Date"),
      render: (_, record) => (
        <TooltipForForm
          title={
            <Stack gap={1}>
              <span>
                Viewed At:{" "}
                {record.viewedAt
                  ? moment(record.viewedAt).format("YYYY-MM-DD hh:mm:ss")
                  : "-"}
              </span>
              <span>
                Answered At:{" "}
                {record.answeredAt
                  ? moment(record.answeredAt).format("YYYY-MM-DD hh:mm:ss")
                  : "-"}
              </span>
              <span>
                Sent At:{" "}
                {record.sentAt
                  ? moment(record.sentAt).format("YYYY-MM-DD hh:mm:ss")
                  : "-"}
              </span>
            </Stack>
          }
        >
          <span>
            {record.answeredAt
              ? `Answered At: ${moment(record.answeredAt).format("YYYY-MM-DD")}`
              : record.viewedAt
              ? `Viewed At: ${moment(record.viewedAt).format("YYYY-MM-DD")}`
              : ""}
          </span>
        </TooltipForForm>
      ),
    },
    {
      id: "invalid",
      label: localized("Invalid"),
      render: (_, record) => {
        if (record.invalid && record.invalidReason) {
          return (
            <TooltipForForm
              title={
                <Stack
                  px={2}
                  py={1}
                  gap={1}
                  component="ul"
                  sx={{ listStyle: "decimal" }}
                >
                  <li>{record.invalidReason}</li>
                </Stack>
              }
            >
              <Chip color="error" label="Invalid" data-testid={"Invalid"} />
            </TooltipForForm>
          );
        } else if (record?.invalid === false) {
          return <Chip color="success" label="Valid" data-testid={"Valid"} />;
        }
        return <div></div>;
      },
    },
    {
      id: "answers",
      label: localized("Answers"),
      render: (_, submit) => (
        <LoadingButton
          variant="contained"
          onClick={async () => {
            const { answers, questions } = await getSubmitAnswers({
              eventId: submit.surveyEventId,
              submitId: submit.id,
            });
            await window.webApp.toggleDialog({
              title: localized("Survey Answer"),
              element: <SurveySubmitAnswerView answers={answers} questions={questions} />,
              minWidth: 800,
              onOk: async () => {},
              okText: localized("Close"),
              noCancel: true,
              open: true,
            });
          }}
        >
          {localized("View")}
        </LoadingButton>
      ),
    },
  ];

  const filters: FilterGroup<SurveyEventSubmit>[] = [
    {
      title: localized("Filter By Date"),
      items: [
        {
          name: "viewedAt",
          type: FilterType.date_range,
        },
        {
          name: "sentAt",
          type: FilterType.date_range,
        },
        {
          name: "answeredAt",
          type: FilterType.date_range,
        },
      ],
    },
    {
      title: localized("Filter By Status"),
      items: [
        {
          name: "status",
          type: FilterType.selection,
          options: Object.values(SurveyEventStatusEnum).map((status) => ({
            label: titleStr(status),
            value: status,
          })),
          multiple: true,
        },
        {
          name: "invalid",
          type: FilterType.selection,
          options: [
            {
              label: localized("Valid"),
              value: "false",
            },
            {
              label: localized("Invalid"),
              value: "true",
            },
          ],
        },
      ],
    },
  ];

  const tableRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    onSubmit() {
      return Promise.resolve([]);
    },
  }));

  const loadData = async (param: Pagination) => {
    if (param.filter?.and) {
      param.filter.and = param.filter.and.map((condition) => {
        if (condition.key === "invalid") {
          return {
            ...condition,
            values: condition.values.map((v) => v === "true"),
          };
        }
        return condition;
      });
    }
    const res: any = await getSurveyEventSubmitsById({
      id: props.id,
      attributes: [
        "pk",
        "sk",
        "data",
        "uid",
        "name",
        "device",
        "status",
        "viewedAt",
        "answeredAt",
        "sentAt",
        "invalid",
        "invalidReason",
      ],
      ...param,
    });
    return res;
  };

  const onDelete = (r: SurveyEventSubmit) => {
    return deleteSurveyEventSubmit(r.id, r.companyId);
  };

  return (
    <div>
      <PaginatedTableList<SurveyEventSubmit>
        headCells={headCells}
        ref={tableRef}
        uid="uid"
        addButtonClassName="survey-customer-add-btn"
        onLoad={loadData}
        filters={filters}
        initialSortKey={"answeredAt"}
        initialOrder={"desc"}
        onDelete={onDelete}
        searchKeys={["id"]}
        searchText="Search by full submit id"
        searchFilterType={PaginationFilterType.equal}
      />
    </div>
  );
});
